import {
  getDatabase,
  ref,
  child,
  push,
  update,
  set,
  remove,
  onValue,
  get
} from 'firebase/database';
import { auth, app } from './init';
import authStore from './auth';
import { v4 } from 'uuid';
import { PackageT } from '@/modules/nodesCanvasModule/components/storeClasses/packageT';
import { reactive } from 'vue';
import { defaultCustomPkgText } from '@/modules/codeViewModule/customCodeProcessing/processCustomPackageText';

const db = getDatabase(app);

const dbRef = (path: string) => ref(db, path);

// NOTE: data will be written over. Use with care, ensure packageDetails is valid
const writePackage = (packageDetails: PackageT) => {
  // Write to UID/packages/packageID
  if (!authStore.uid) return;
  const std = JSON.parse(JSON.stringify(packageDetails));
  set(ref(db, `${authStore.uid}/packages/${packageDetails.id}`), std);
};

// Current data structure is
// UID/workspaces/workspaceId/packages/packageId => packageDetails
const addPackageToWorkspace = (
  workspaceId: ReturnType<typeof v4>,
  packageDetails: PackageT
) => {
  if (!authStore.uid) return;
  const workspaceRef = dbRef(
    `${authStore.uid}/workspaces/${workspaceId}/packages/${packageDetails.id}`
  );
  const std = JSON.parse(JSON.stringify(packageDetails));
  return set(workspaceRef, std);
};

const setWorkspaceName = (workspaceId: ReturnType<typeof v4>, name: string) => {
  if (!authStore.uid) return;
  const workspaceNameRef = dbRef(
    `${authStore.uid}/workspaces/${workspaceId}/name`
  );
  return set(workspaceNameRef, name);
};

type DBpath = string;
const updateWorkspaceMeta = (
  workspaceId: V4UUID,
  meta: Record<DBpath, any>
) => {
  if (!authStore.uid) return;
  const metaRef = dbRef(`${authStore.uid}/workspaces/${workspaceId}/meta`);
  return update(metaRef, meta);
};

const deleteWorkspace = (workspaceId: V4UUID) => {
  if (!authStore.uid) return;
  return remove(dbRef(`${authStore.uid}/workspaces/${workspaceId}`));
};

const workspaceState = (workspaceId: V4UUID) => {
  const stateWorkspace: { info?: any } = reactive({});
  const workspaceRef = dbRef(`${authStore.uid}/workspaces/${workspaceId}`);
  onValue(workspaceRef, (snapshot) => {
    stateWorkspace.info = snapshot.val();
  });
  return stateWorkspace;
};

const workspaceStateAsync = async (workspaceId: V4UUID) => {
  const stateWorkspace: { info?: any } = reactive({});
  const workspaceRef = dbRef(`${authStore.uid}/workspaces/${workspaceId}`);
  const snapshot = await get(workspaceRef);
  return snapshot.val();
};

const getWorkspacesStateOnce = async () => {
  // Force wait until authStore.uid is valid (due to async issues)
  if (!authStore.uid) {
    console.warn("couldn't get all workspaces not authenticated");
    return undefined;
  }
  const allWorkspaces = dbRef(`${authStore.uid}/workspaces`);
  const snapshot = await get(allWorkspaces);
  if (!snapshot.exists()) return undefined;
  if (!snapshot.val()) return undefined;
  return snapshot.val();
};

const allWorkspaceState: {
  info?: any;
  isSet: boolean;
  listeners: Array<() => void>;
} = reactive({
  isSet: false,
  listeners: []
});

let isUsedFlag = false;
/**
 * If info: null | undefined, then collection is invalid
 * @returns
 */

const forceAllWorkplacesStateSingletonReset = () => {
  isUsedFlag = false;
  allWorkspaceState.info = undefined;
  allWorkspaceState.isSet = false;
  for (let listenerFunc of allWorkspaceState.listeners) {
    listenerFunc();
  }
};

const allWorkspacesStateSingleton = () => {
  if (isUsedFlag) return allWorkspaceState;
  // Force wait until authStore.uid is valid (due to async issues)
  if (!authStore.uid) return allWorkspaceState;
  const allWorkspaces = dbRef(`${authStore.uid}/workspaces`);
  onValue(allWorkspaces, (snapshot) => {
    allWorkspaceState.info = snapshot.val();
    allWorkspaceState.isSet = true;
    for (let listenerFunc of allWorkspaceState.listeners) {
      listenerFunc();
    }
  });
  isUsedFlag = true;
  return allWorkspaceState;
};
/**
 * Adds a function that is called each time the workspace state singleton changes
 * @param listenerFunc
 */
const addWorkspaceStateListener = (listenerFunc: () => void) => {
  allWorkspaceState.listeners.push(listenerFunc);
};

// Clones a workspace, and gives it a new ID, setting it to database
const writeWorkspaceClone = (workspaceId: V4UUID) => {
  try {
    const toClone = allWorkspacesStateSingleton()?.info[workspaceId];
    if (!toClone) return;
    const newId = v4();
    setWorkspaceName(newId, toClone.name);
    updateWorkspaceMeta(newId, {
      ...toClone.meta,
      timeCreated: new Date().getTime()
    });
    if ('packages' in toClone)
      Promise.allSettled([
        ...Object.entries(toClone.packages).map(([_, val]: [string, any]) =>
          addPackageToWorkspace(newId, { ...val, id: v4() })
        )
      ]);
  } catch (error) {
    console.warn('clone failed', error);
  }
};

const mockData: PackageT = {
  name: 'mockname',
  id: v4(),
  nodes: [],
  topics: [],
  nodeTemplates: [],
  msgs: [],
  services: [],
  favouriteElems: [],
  customText: { ...defaultCustomPkgText },
  launchFiles: [],
  description: 'No description found'
};

type basicEmptyPackage = {
  name: string;
  id: V4UUID;
  x?: number;
  y?: number;
  robotPackage?: boolean;
};

const createEmptyPackage = (args: basicEmptyPackage): PackageT => {
  return new PackageT(
    args.name,
    args.id,
    args.robotPackage || false,
    [],
    [],
    [],
    [],
    [],
    []
  );
};

export {
  writePackage,
  addPackageToWorkspace,
  workspaceState,
  workspaceStateAsync,
  updateWorkspaceMeta,
  setWorkspaceName,
  allWorkspacesStateSingleton,
  writeWorkspaceClone,
  deleteWorkspace,
  createEmptyPackage,
  dbRef,
  getWorkspacesStateOnce,
  forceAllWorkplacesStateSingletonReset,
  addWorkspaceStateListener
};

type V4UUID = ReturnType<typeof v4>;

export { DBpath, mockData, V4UUID };
